import { useState } from 'react';
import { motion } from 'framer-motion';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Slider } from '../components/ui/slider';
import { toast } from 'sonner';
import { 
  Calculator, 
  DollarSign, 
  Percent, 
  Calendar,
  TrendingDown,
  ArrowRight,
  CheckCircle
} from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function CalculatorPage() {
  const [loanAmount, setLoanAmount] = useState(500000);
  const [interestRate, setInterestRate] = useState(6.0);
  const [loanTerm, setLoanTerm] = useState(30);
  const [showResults, setShowResults] = useState(false);
  const [showLeadForm, setShowLeadForm] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [leadSubmitted, setLeadSubmitted] = useState(false);
  const [leadForm, setLeadForm] = useState({
    name: '',
    email: '',
    phone: ''
  });

  // Calculate mortgage repayments
  const calculateMortgage = () => {
    const principal = loanAmount;
    const monthlyRate = interestRate / 100 / 12;
    const numberOfPayments = loanTerm * 12;
    
    const monthlyPayment = principal * 
      (monthlyRate * Math.pow(1 + monthlyRate, numberOfPayments)) / 
      (Math.pow(1 + monthlyRate, numberOfPayments) - 1);
    
    const totalPayment = monthlyPayment * numberOfPayments;
    const totalInterest = totalPayment - principal;
    
    return {
      monthlyPayment: monthlyPayment || 0,
      totalPayment: totalPayment || 0,
      totalInterest: totalInterest || 0
    };
  };

  const results = calculateMortgage();

  const handleCalculate = () => {
    setShowResults(true);
    setTimeout(() => setShowLeadForm(true), 500);
  };

  const handleLeadSubmit = async (e) => {
    e.preventDefault();
    
    if (!leadForm.name || !leadForm.email || !leadForm.phone) {
      toast.error('Please fill in all fields');
      return;
    }

    setIsSubmitting(true);

    try {
      await axios.post(`${API_URL}/api/calculator-leads`, {
        ...leadForm,
        loan_amount: loanAmount,
        interest_rate: interestRate,
        loan_term: loanTerm,
        monthly_repayment: results.monthlyPayment,
        total_interest: results.totalInterest
      });
      setLeadSubmitted(true);
      toast.success('Thanks! A broker will contact you shortly.');
    } catch (error) {
      console.error('Lead submission error:', error);
      toast.error('Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-AU', {
      style: 'currency',
      currency: 'AUD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  return (
    <div className="pt-20" data-testid="calculator-page">
      {/* Hero Section */}
      <section className="py-16 md:py-24 gradient-hero">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-3xl mx-auto"
          >
            <span className="inline-block px-4 py-2 bg-[#374151]/10 text-[#374151] rounded-full text-sm font-medium mb-6">
              Free Tool
            </span>
            <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold text-[#374151] leading-tight mb-6">
              Mortgage Repayment Calculator
            </h1>
            <p className="text-lg md:text-xl text-gray-500 leading-relaxed">
              Estimate your monthly repayments and see how much you could borrow. Get personalised rates from our brokers.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Calculator Section */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-6xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Calculator Inputs */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              className="bg-[#F9FAFB] rounded-2xl p-8"
            >
              <h2 className="font-heading text-2xl font-semibold text-[#374151] mb-8">
                Enter Your Details
              </h2>

              <div className="space-y-8">
                {/* Loan Amount */}
                <div className="space-y-4">
                  <div className="flex justify-between items-center">
                    <Label className="text-[#374151] font-medium flex items-center gap-2">
                      <DollarSign className="w-4 h-4" />
                      Loan Amount
                    </Label>
                    <span className="font-heading font-semibold text-[#374151]">
                      {formatCurrency(loanAmount)}
                    </span>
                  </div>
                  <Slider
                    value={[loanAmount]}
                    onValueChange={(value) => setLoanAmount(value[0])}
                    min={100000}
                    max={2000000}
                    step={10000}
                    className="py-4"
                    data-testid="loan-amount-slider"
                  />
                  <div className="flex justify-between text-sm text-gray-500">
                    <span>$100k</span>
                    <span>$2M</span>
                  </div>
                </div>

                {/* Interest Rate */}
                <div className="space-y-4">
                  <div className="flex justify-between items-center">
                    <Label className="text-[#374151] font-medium flex items-center gap-2">
                      <Percent className="w-4 h-4" />
                      Interest Rate
                    </Label>
                    <span className="font-heading font-semibold text-[#374151]">
                      {interestRate.toFixed(2)}%
                    </span>
                  </div>
                  <Slider
                    value={[interestRate]}
                    onValueChange={(value) => setInterestRate(value[0])}
                    min={2}
                    max={10}
                    step={0.05}
                    className="py-4"
                    data-testid="interest-rate-slider"
                  />
                  <div className="flex justify-between text-sm text-gray-500">
                    <span>2%</span>
                    <span>10%</span>
                  </div>
                </div>

                {/* Loan Term */}
                <div className="space-y-4">
                  <div className="flex justify-between items-center">
                    <Label className="text-[#374151] font-medium flex items-center gap-2">
                      <Calendar className="w-4 h-4" />
                      Loan Term
                    </Label>
                    <span className="font-heading font-semibold text-[#374151]">
                      {loanTerm} years
                    </span>
                  </div>
                  <Slider
                    value={[loanTerm]}
                    onValueChange={(value) => setLoanTerm(value[0])}
                    min={5}
                    max={30}
                    step={1}
                    className="py-4"
                    data-testid="loan-term-slider"
                  />
                  <div className="flex justify-between text-sm text-gray-500">
                    <span>5 years</span>
                    <span>30 years</span>
                  </div>
                </div>

                <Button
                  onClick={handleCalculate}
                  className="w-full bg-[#6B7280] hover:bg-[#4B5563] text-white rounded-full py-6 text-lg font-medium transition-all duration-300 hover:-translate-y-1 hover:shadow-lg"
                  data-testid="calculate-button"
                >
                  <Calculator className="w-5 h-5 mr-2" />
                  Calculate Repayments
                </Button>
              </div>
            </motion.div>

            {/* Results */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="space-y-6"
            >
              {showResults ? (
                <>
                  <div className="bg-[#374151] rounded-2xl p-8 text-white">
                    <h3 className="font-heading text-xl font-semibold mb-6">Your Estimated Repayments</h3>
                    
                    <div className="space-y-6">
                      <div className="text-center py-6 border-b border-white/20">
                        <p className="text-white/70 mb-2">Monthly Repayment</p>
                        <p className="font-heading text-5xl font-bold" data-testid="monthly-result">
                          {formatCurrency(results.monthlyPayment)}
                        </p>
                      </div>

                      <div className="grid grid-cols-2 gap-6">
                        <div className="text-center">
                          <p className="text-white/70 text-sm mb-1">Total Repayment</p>
                          <p className="font-heading text-xl font-semibold">
                            {formatCurrency(results.totalPayment)}
                          </p>
                        </div>
                        <div className="text-center">
                          <p className="text-white/70 text-sm mb-1">Total Interest</p>
                          <p className="font-heading text-xl font-semibold text-[#6B7280]">
                            {formatCurrency(results.totalInterest)}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Lead Capture Form */}
                  {showLeadForm && !leadSubmitted && (
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="bg-[#F3F4F6] rounded-2xl p-8"
                    >
                      <div className="flex items-center gap-3 mb-4">
                        <TrendingDown className="w-6 h-6 text-[#6B7280]" />
                        <h3 className="font-heading text-xl font-semibold text-[#374151]">
                          Could You Pay Less?
                        </h3>
                      </div>
                      <p className="text-gray-500 mb-6">
                        Our brokers compare rates from 40+ lenders. Get a personalised quote – it's free and takes 2 minutes.
                      </p>

                      <form onSubmit={handleLeadSubmit} className="space-y-4" data-testid="lead-form">
                        <Input
                          placeholder="Your name"
                          value={leadForm.name}
                          onChange={(e) => setLeadForm({...leadForm, name: e.target.value})}
                          className="h-12 rounded-lg bg-white"
                          data-testid="lead-name"
                        />
                        <Input
                          type="email"
                          placeholder="Email address"
                          value={leadForm.email}
                          onChange={(e) => setLeadForm({...leadForm, email: e.target.value})}
                          className="h-12 rounded-lg bg-white"
                          data-testid="lead-email"
                        />
                        <Input
                          type="tel"
                          placeholder="Phone number"
                          value={leadForm.phone}
                          onChange={(e) => setLeadForm({...leadForm, phone: e.target.value})}
                          className="h-12 rounded-lg bg-white"
                          data-testid="lead-phone"
                        />
                        <Button
                          type="submit"
                          disabled={isSubmitting}
                          className="w-full bg-[#374151] hover:bg-[#244038] text-white rounded-full py-6 font-medium"
                          data-testid="lead-submit"
                        >
                          {isSubmitting ? 'Sending...' : 'Get My Free Quote'}
                          <ArrowRight className="w-5 h-5 ml-2" />
                        </Button>
                      </form>
                    </motion.div>
                  )}

                  {leadSubmitted && (
                    <motion.div
                      initial={{ opacity: 0, scale: 0.95 }}
                      animate={{ opacity: 1, scale: 1 }}
                      className="bg-green-50 border border-green-200 rounded-2xl p-8 text-center"
                      data-testid="lead-success"
                    >
                      <CheckCircle className="w-12 h-12 text-green-600 mx-auto mb-4" />
                      <h3 className="font-heading text-xl font-semibold text-[#374151] mb-2">
                        Thank You!
                      </h3>
                      <p className="text-gray-500">
                        One of our brokers will contact you within 24 hours with personalised rate options.
                      </p>
                    </motion.div>
                  )}
                </>
              ) : (
                <div className="bg-[#F9FAFB] rounded-2xl p-8 h-full flex items-center justify-center">
                  <div className="text-center">
                    <Calculator className="w-16 h-16 text-gray-500/30 mx-auto mb-4" />
                    <p className="text-gray-500">
                      Adjust the sliders and click calculate to see your estimated repayments
                    </p>
                  </div>
                </div>
              )}
            </motion.div>
          </div>
        </div>
      </section>

      {/* Info Section */}
      <section className="py-16 bg-[#F9FAFB]">
        <div className="max-w-6xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-14 h-14 bg-[#F3F4F6] rounded-xl flex items-center justify-center mx-auto mb-4">
                <Calculator className="w-7 h-7 text-[#374151]" />
              </div>
              <h3 className="font-heading font-semibold text-lg text-[#374151] mb-2">Estimate Only</h3>
              <p className="text-sm text-gray-500">
                This calculator provides estimates only. Actual rates and repayments may vary.
              </p>
            </div>
            <div className="text-center">
              <div className="w-14 h-14 bg-[#F3F4F6] rounded-xl flex items-center justify-center mx-auto mb-4">
                <DollarSign className="w-7 h-7 text-[#374151]" />
              </div>
              <h3 className="font-heading font-semibold text-lg text-[#374151] mb-2">Principal & Interest</h3>
              <p className="text-sm text-gray-500">
                Calculations assume principal and interest repayments over the full loan term.
              </p>
            </div>
            <div className="text-center">
              <div className="w-14 h-14 bg-[#F3F4F6] rounded-xl flex items-center justify-center mx-auto mb-4">
                <Percent className="w-7 h-7 text-[#374151]" />
              </div>
              <h3 className="font-heading font-semibold text-lg text-[#374151] mb-2">Get Real Rates</h3>
              <p className="text-sm text-gray-500">
                Speak to a broker to get actual rates based on your circumstances.
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
